import * as React from 'react';
import { createContext, useContext, useRef, useState, useEffect, useCallback } from 'react';
import { cn } from '@/lib/utils';

type CollapseState = 'expanded' | 'collapsed';
type RightActionsPlacement = 'primary' | 'secondary';

interface ResponsiveToolbarContextValue {
  rightCollapseState: CollapseState;
  leftCollapseState: CollapseState;
  rightActionsPlacement: RightActionsPlacement;
  rightSectionWrapped: boolean;
}

const ResponsiveToolbarContext = createContext<ResponsiveToolbarContextValue>({
  rightCollapseState: 'expanded',
  leftCollapseState: 'expanded',
  rightActionsPlacement: 'primary',
  rightSectionWrapped: false,
});

export function useResponsiveToolbar() {
  return useContext(ResponsiveToolbarContext);
}

type ResponsiveToolbarProps = React.ComponentPropsWithoutRef<'div'> & {
  children: React.ReactNode;
};

/**
 * Has the right section dropped onto its own row?
 *
 * Compared with a 1px slack because the two sections are centred against each
 * other on a shared row and can differ by a sub-pixel; a real wrap moves the
 * right section a full row height down.
 */
function isSectionWrapped(leftOffsetTop: number, rightOffsetTop: number): boolean {
  return rightOffsetTop > leftOffsetTop + 1;
}

/**
 * ResponsiveToolbar provides responsive collapse behavior for toolbar items.
 *
 * When horizontal space is limited:
 * 1. Right-aligned items collapse to icon-only (with chevron for dropdowns)
 * 2. If still not enough space, the toolbar wraps to two rows
 *
 * Usage:
 * <ResponsiveToolbar>
 *   <ToolbarSection position="left">...</ToolbarSection>
 *   <ToolbarSection position="right">...</ToolbarSection>
 * </ResponsiveToolbar>
 */
export function ResponsiveToolbar({ children, className, ...props }: ResponsiveToolbarProps) {
  const containerRef = useRef<HTMLDivElement>(null);
  const leftRef = useRef<HTMLDivElement>(null);
  const rightRef = useRef<HTMLDivElement>(null);
  const [rightCollapseState, setRightCollapseState] = useState<CollapseState>('expanded');
  const [leftCollapseState, setLeftCollapseState] = useState<CollapseState>('expanded');
  const [rightActionsPlacement, setRightActionsPlacement] = useState<RightActionsPlacement>('primary');
  const [rightSectionWrapped, setRightSectionWrapped] = useState(false);

  // Width snapshots for staged collapse/expand hysteresis.
  const rightExpandedWidthRef = useRef<number>(0);
  const rightCollapsedWidthRef = useRef<number>(0);
  const leftExpandedWidthRef = useRef<number>(0);
  const leftCollapsedWidthRef = useRef<number>(0);

  const checkOverflow = useCallback(() => {
    const container = containerRef.current;
    const left = leftRef.current;
    const right = rightRef.current;
    if (!container || !left || !right) return;

    // Get actual widths
    const containerWidth = container.clientWidth;
    const containerPadding = 16; // px-2 = 8px each side
    const availableWidth = containerWidth - containerPadding;
    const gap = 8; // gap-2 = 8px

    const rightChildren = right.querySelectorAll('[data-toolbar-item]');
    const sectionGap = gap;
    const wrapped = isSectionWrapped(left.offsetTop, right.offsetTop);
    if (rightSectionWrapped !== wrapped) {
      setRightSectionWrapped(wrapped);
    }

    // Measure current section widths
    const leftWidth = left.scrollWidth;
    const rightCurrentContentWidth = right.scrollWidth;
    const secondaryRowGroups = left.querySelectorAll<HTMLElement>('[data-toolbar-secondary-row-group]');
    const secondaryRowCurrentWidth = Array.from(secondaryRowGroups).reduce((total, group) => total + group.scrollWidth, 0);
    const leftBaseWidth = Math.max(0, leftWidth - secondaryRowCurrentWidth);
    const currentTotal = leftBaseWidth + sectionGap + rightCurrentContentWidth;
    // Collapse early (before actual overflow) to avoid a visible wrap-then-collapse flash.
    const collapseBuffer = 40;
    const isOverflowing = currentTotal + collapseBuffer > availableWidth;

    // Left-collapsible width is explicitly opt-in via data attribute.
    const leftCollapsibleGroups = left.querySelectorAll<HTMLElement>('[data-toolbar-left-collapsible-group]');
    const leftCollapsibleItems = left.querySelectorAll<HTMLElement>('[data-toolbar-left-collapsible]');
    const leftCollapsibleCurrentWidth = leftCollapsibleGroups.length > 0
      ? Array.from(leftCollapsibleGroups).reduce((total, group) => total + group.scrollWidth, 0)
      : Array.from(leftCollapsibleItems).reduce((total, item) => total + item.scrollWidth, 0)
        + Math.max(0, leftCollapsibleItems.length - 1) * gap;
    const hasLeftCollapsible = leftCollapsibleItems.length > 0;
    const leftFixedWidth = hasLeftCollapsible
      ? Math.max(0, leftBaseWidth - leftCollapsibleCurrentWidth)
      : leftBaseWidth;

    // Track measured widths for staged expansion calculations.
    if (rightCollapseState === 'expanded') {
      rightExpandedWidthRef.current = Math.max(rightExpandedWidthRef.current, rightCurrentContentWidth);
    } else {
      rightCollapsedWidthRef.current = Math.max(rightCollapsedWidthRef.current, rightCurrentContentWidth);
    }

    if (hasLeftCollapsible) {
      if (leftCollapseState === 'expanded') {
        leftExpandedWidthRef.current = leftCollapsibleCurrentWidth;
      } else {
        leftCollapsedWidthRef.current = leftCollapsibleCurrentWidth;
      }
    }

    // Fallback estimates before first measurement for each state.
    const expandedItemWidth = 80; // Approximate width for icon + label button
    const collapsedItemWidth = 40; // Approximate width for icon-only/compact buttons
    const rightItemCount = rightChildren.length;
    const rightItemGapsEstimate = Math.max(0, rightItemCount - 1) * gap;
    const rightExpandedEstimate = rightItemCount * expandedItemWidth + rightItemGapsEstimate;
    const rightCollapsedEstimate = rightItemCount * collapsedItemWidth + rightItemGapsEstimate;

    const leftExpandedEstimate = leftCollapsibleItems.length > 0
      ? leftCollapsibleItems.length * expandedItemWidth + Math.max(0, leftCollapsibleItems.length - 1) * gap
      : 0;
    const leftCollapsedEstimate = leftCollapsibleItems.length > 0
      ? leftCollapsibleItems.length * collapsedItemWidth + Math.max(0, leftCollapsibleItems.length - 1) * gap
      : 0;

    const rightExpandedWidth = rightExpandedWidthRef.current > 0
      ? rightExpandedWidthRef.current
      : rightExpandedEstimate;
    const rightCollapsedWidth = rightCollapsedWidthRef.current > 0
      ? rightCollapsedWidthRef.current
      : rightCollapsedEstimate;
    const leftExpandedWidth = leftExpandedWidthRef.current > 0
      ? leftExpandedWidthRef.current
      : leftExpandedEstimate;
    const leftCollapsedWidth = leftCollapsedWidthRef.current > 0
      ? leftCollapsedWidthRef.current
      : leftCollapsedEstimate;

    // Expand threshold must exceed collapse threshold to avoid a flicker loop.
    // Collapse fires at expandedTotal + collapseBuffer > available,
    // so we need expandedTotal + expandBuffer <= available with expandBuffer > collapseBuffer.
    const expandBuffer = collapseBuffer + 20;

    // Stage 1: collapse right section first.
    if (rightCollapseState === 'expanded') {
      if (isOverflowing) {
        setRightCollapseState('collapsed');
      }
      if (rightActionsPlacement !== 'primary') {
        setRightActionsPlacement('primary');
      }
      return;
    }

    // Stage 2: if still overflowing, collapse designated left controls.
    if (isOverflowing) {
      if (hasLeftCollapsible && leftCollapseState === 'expanded') {
        setLeftCollapseState('collapsed');
      }
      return;
    }

    // Stage 3: after both collapse stages, move right actions to row 2 only
    // when a primary-row layout would still overflow.
    const bothCollapsed = hasLeftCollapsible
      ? leftCollapseState === 'collapsed'
      : true;
    const collapsedPrimaryTotal = leftFixedWidth + leftCollapsedWidth + sectionGap + rightCollapsedWidth;

    if (rightCollapseState === 'collapsed' && bothCollapsed) {
      if (rightActionsPlacement === 'primary') {
        if (collapsedPrimaryTotal > availableWidth) {
          setRightActionsPlacement('secondary');
          return;
        }
      } else {
        if (collapsedPrimaryTotal + expandBuffer <= availableWidth) {
          setRightActionsPlacement('primary');
        }
        return;
      }
    }

    if (rightActionsPlacement !== 'primary') {
      setRightActionsPlacement('primary');
      return;
    }

    // Expand left collapsible controls before expanding right section back.
    if (hasLeftCollapsible && leftCollapseState === 'collapsed') {
      const leftExpandedTotal = leftFixedWidth + leftExpandedWidth + sectionGap + rightCollapsedWidth;
      if (leftExpandedTotal + expandBuffer <= availableWidth) {
        setLeftCollapseState('expanded');
        return;
      }
    }

    // Finally, expand right section if there is still enough room.
    const effectiveLeftWidth = hasLeftCollapsible
      ? (leftCollapseState === 'collapsed' ? leftCollapsedWidth : leftExpandedWidth)
      : 0;
    const rightExpandedTotal = leftFixedWidth + effectiveLeftWidth + sectionGap + rightExpandedWidth;

    if (rightExpandedTotal + expandBuffer <= availableWidth) {
      setRightCollapseState('expanded');
    }
  }, [leftCollapseState, rightActionsPlacement, rightCollapseState, rightSectionWrapped]);

  // Use a ref so effects don't re-run (and re-create observers) on every state change.
  const checkOverflowRef = useRef(checkOverflow);
  checkOverflowRef.current = checkOverflow;

  useEffect(() => {
    const container = containerRef.current;
    if (!container) return;

    let rafId: number | null = null;
    const observer = new ResizeObserver(() => {
      // Debounce with RAF to coalesce multiple resize events per frame.
      if (rafId !== null) cancelAnimationFrame(rafId);
      rafId = requestAnimationFrame(() => {
        rafId = null;
        checkOverflowRef.current();
      });
    });

    // Only observe the container — observing left/right sections causes feedback
    // loops because collapsing buttons changes their size, re-triggering the observer.
    observer.observe(container);

    // Initial check
    requestAnimationFrame(() => checkOverflowRef.current());

    return () => {
      observer.disconnect();
      if (rafId !== null) cancelAnimationFrame(rafId);
    };
  }, []);

  // Re-check when children change
  useEffect(() => {
    const rafId = requestAnimationFrame(() => checkOverflowRef.current());
    return () => cancelAnimationFrame(rafId);
  }, [children]);

  // A staged collapse changes the sections' widths without resizing the
  // container, so the ResizeObserver above never fires for it and the wrap
  // flag measured during that pass is already stale by the time it lands.
  // Re-measure just that flag once the new layout is on screen — cheap (two
  // offsetTop reads) and free of feedback, because nothing keyed off it
  // changes the sections' own box.
  useEffect(() => {
    const rafId = requestAnimationFrame(() => {
      const left = leftRef.current;
      const right = rightRef.current;
      if (!left || !right) return;
      setRightSectionWrapped(isSectionWrapped(left.offsetTop, right.offsetTop));
    });
    return () => cancelAnimationFrame(rafId);
  }, [leftCollapseState, rightCollapseState, rightActionsPlacement]);

  return (
    <ResponsiveToolbarContext.Provider value={{ rightCollapseState, leftCollapseState, rightActionsPlacement, rightSectionWrapped }}>
      <div
        ref={containerRef}
        {...props}
        // Read by the Windows integrated-title-bar rules in src/index.css: the
        // caption-button strip is only shared with the FIRST row, so once the
        // right cluster has wrapped it may reclaim the width reserved for it.
        data-toolbar-right-wrapped={rightSectionWrapped ? 'true' : 'false'}
        className={cn(
          'flex flex-wrap items-center gap-2 px-2 py-1',
          className
        )}
      >
        <ToolbarSectionRefs leftRef={leftRef} rightRef={rightRef}>
          {children}
        </ToolbarSectionRefs>
      </div>
    </ResponsiveToolbarContext.Provider>
  );
}

// Internal context to pass refs to sections
interface ToolbarSectionRefsContextValue {
  leftRef: React.RefObject<HTMLDivElement | null>;
  rightRef: React.RefObject<HTMLDivElement | null>;
}

const ToolbarSectionRefsContext = createContext<ToolbarSectionRefsContextValue | null>(null);

function ToolbarSectionRefs({
  children,
  leftRef,
  rightRef,
}: {
  children: React.ReactNode;
  leftRef: React.RefObject<HTMLDivElement | null>;
  rightRef: React.RefObject<HTMLDivElement | null>;
}) {
  return (
    <ToolbarSectionRefsContext.Provider value={{ leftRef, rightRef }}>
      {children}
    </ToolbarSectionRefsContext.Provider>
  );
}

interface ToolbarSectionProps {
  children: React.ReactNode;
  position: 'left' | 'right';
  className?: string;
}

/**
 * ToolbarSection groups toolbar items. The 'right' section will collapse
 * to icon-only when space is limited.
 */
export function ToolbarSection({ children, position, className }: ToolbarSectionProps) {
  const refs = useContext(ToolbarSectionRefsContext);
  const ref = position === 'left' ? refs?.leftRef : refs?.rightRef;

  return (
    <div
      ref={ref as React.RefObject<HTMLDivElement>}
      data-toolbar-section={position}
      className={cn(
        'flex items-center gap-2',
        position === 'right' && 'ml-auto',
        className
      )}
    >
      {children}
    </div>
  );
}
